/**
 * Payment endpoints for the API contract.
 *
 * These endpoints handle subscription management across Stripe and RevenueCat.
 *
 * @see packages/core/api-contract/README.md for the full endpoint list
 */
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Effect, Schema } from 'effect';
import { UserAuthMiddleware } from './middleware';
import { BadRequestError, ConflictError, InternalServerError } from './errors';

// Import schemas from legacy client where available
import {
  PostCreatePaidSubscription200,
  PostResubscribe200,
  ReferralSubscriptionView,
  RevenueCatSubscriptionView,
  StripeSubscriptionView,
} from './legacy-client.gen';

/**
 * The generated `StripeSubscriptionView` plus the one field the plan page
 * needs: the pricing-test arm the subscription was created under.
 *
 * Composed here rather than regenerated into `legacy-client.gen.ts`, which is
 * generated from the deployed legacy OpenAPI schema and cannot describe a
 * response that is not deployed yet. Existing clients ignore the additive
 * field; typed callers get it without a generated-file/deployment cycle.
 *
 * Only a recognized arm or `null` ever reaches the browser — the server
 * validates the Stripe metadata through `decodePricingVariant` first, so this
 * is never a passthrough of arbitrary metadata. `null` means "not an
 * experiment subscription": every legacy, owed-time and unenrolled
 * subscription.
 *
 * The wire type is deliberately lenient (`string | null | undefined`) while the
 * server stays strict, because client and server deploy independently: a
 * required strict-literal field fails the WHOLE `GetSubscriptions` decode when
 * an older server omits the key, and a future arm name would fail it on older
 * clients. Consumers narrow with `decodePricingVariant`, which treats anything
 * it does not recognize as "not an experiment subscription".
 *
 * Why the plan page needs it: the annual price a monthly subscriber may switch
 * *to* cannot be read off their own subscription, and the live feature flag is
 * the wrong source — it disappears when the experiment ends while the
 * grandfathered price is forever. The stamp on the subscription is durable.
 */
export const StripeSubscriptionViewWithPricingVariant =
  StripeSubscriptionView.pipe(
    Schema.fieldsAssign({
      pricing_variant: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  );

/**
 * The pricing-test arm this account is quoted at RIGHT NOW, as resolved by the
 * API's Mode C ladder: the account's pinned row, else the request's
 * `lis_bucket` bucket, else the experiment's declared fallback arm.
 *
 * This is the paywall's only arm source. It cannot be a client-side feature-flag
 * read: the pin is a per-account database row, and the price a paywall displays
 * has to be the price the checkout endpoints will charge — which is this ladder,
 * evaluated on the same request. See
 * docs/projects/active/edge-experiments-account-pinned-bucketing.md §3.4.
 *
 * - `variant` is the arm whose PRICES and trial the surface renders. Always a
 *   live arm key (never an edge code — the API decodes at its own boundary).
 * - `enrolled` says whether this account is an experiment SUBJECT. False for
 *   the fallback and non-enrolled rungs, which are served a defined price
 *   without being randomized into it. Only an enrolled account may send
 *   `expected_variant`, be stamped into Stripe, or fire an exposure event —
 *   sending the arm for a non-subject would put unrandomized revenue in the
 *   experiment's results.
 *
 * Lenient wire type and an optional field for the same client/server deploy-skew
 * reason as `pricing_variant` above: an older server omits it and a newer arm
 * name must not fail the whole `GetSubscriptions` decode. Consumers narrow with
 * `decodePricingVariant`.
 */
export const PricingArmView = Schema.Struct({
  variant: Schema.String,
  enrolled: Schema.Boolean,
});

/**
 * Local `GetGetSubscriptions200` composed around the extended Stripe view.
 * Field-for-field the generated shape otherwise, plus `pricing_arm`.
 */
const GetSubscriptionsResponse = Schema.Struct({
  referral_subscription: Schema.NullOr(ReferralSubscriptionView).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  revenue_cat_subscription: Schema.NullOr(RevenueCatSubscriptionView),
  stripe_subscription: Schema.NullOr(StripeSubscriptionViewWithPricingVariant),
  pricing_arm: Schema.optional(Schema.NullOr(PricingArmView)),
});

/**
 * GET /payment/subscriptions
 * Returns subscription status across all payment platforms (Stripe, RevenueCat, referrals).
 */
const getSubscriptionsEndpoint = HttpApiEndpoint.get(
  'GetSubscriptions',
  '/subscriptions',
  {
    success: GetSubscriptionsResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get user subscriptions')
  .annotate(
    OpenApi.Description,
    'Returns the current user subscription status across all payment platforms including Stripe, RevenueCat, and referral subscriptions, plus the pricing-test arm this account is currently quoted at.',
  );

export const paymentsGroup = HttpApiGroup.make('Payments')
  .add(getSubscriptionsEndpoint)
  .prefix('/payment');

// ============================================================================
// Stripe Endpoints
// ============================================================================

export const CancelSubscriptionRequest = Schema.Struct({
  reason: Schema.optional(Schema.String),
});

/**
 * POST /stripe/cancel-subscription
 * Cancel the user's active Stripe subscription.
 */
const cancelSubscriptionEndpoint = HttpApiEndpoint.post(
  'CancelStripeSubscription',
  '/cancel-subscription',
  {
    payload: CancelSubscriptionRequest,
    success: Schema.Struct({ success: Schema.Boolean }),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Cancel Stripe subscription')
  .annotate(
    OpenApi.Description,
    'Cancels the current user Stripe subscription. The subscription will remain active until the end of the current billing period.',
  );

export const ModifyPaymentMethodRequest = Schema.Struct({
  payment_method_id: Schema.String,
});

/**
 * POST /stripe/modify-payment-method
 * Update the payment method for the user's Stripe subscription.
 */
const modifyPaymentMethodEndpoint = HttpApiEndpoint.post(
  'ModifyStripePaymentMethod',
  '/modify-payment-method',
  {
    payload: ModifyPaymentMethodRequest,
    success: Schema.Struct({ success: Schema.Boolean }),
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Modify Stripe payment method')
  .annotate(
    OpenApi.Description,
    'Updates the payment method attached to the user Stripe subscription.',
  );

export const ModifySubscriptionRequest = Schema.Struct({
  stripe_price_id: Schema.String,
});

/**
 * POST /stripe/modify-subscription
 * Change the subscription plan (upgrade/downgrade).
 *
 * `ConflictError` is the pricing-test transition rejection, matching the
 * checkout endpoints' vocabulary: the submitted target is not the one
 * transition this subscription's arm allows, so nothing is changed in Stripe.
 */
const modifySubscriptionEndpoint = HttpApiEndpoint.post(
  'ModifyStripeSubscription',
  '/modify-subscription',
  {
    payload: ModifySubscriptionRequest,
    success: Schema.Struct({ success: Schema.Boolean }),
    error: [BadRequestError, ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Modify Stripe subscription')
  .annotate(
    OpenApi.Description,
    'Changes the user subscription plan to a different price tier.',
  );

/**
 * Ad-attribution params, attached to the Stripe customer for ad-spend
 * tracking. Both checkout endpoints carry them; marketing reconciles ad spend
 * against `google_click_id` / `facebook_click_id` on the customer object.
 */
const CheckoutAttributionFields = {
  facebook_click_cookie: Schema.optional(Schema.NullOr(Schema.String)),
  facebook_click_id: Schema.optional(Schema.NullOr(Schema.String)),
  facebook_pixel_cookie: Schema.optional(Schema.NullOr(Schema.String)),
  google_click_id: Schema.optional(Schema.NullOr(Schema.String)),
};

/**
 * The pricing-test-v1 quote fields both checkout endpoints accept.
 *
 * The server derives the permitted price and trial from its own flag read;
 * these are cross-checks, not inputs. They are optional only so the backend
 * can deploy ahead of the paywall client — omitting `expected_variant` skips
 * the client cross-check, it never skips price/trial validation.
 *
 * `checkout_attempt_id` is an opaque client id reused across retries of one
 * displayed quote. The server binds it to the authenticated user and the
 * resolved quote when deriving the Stripe idempotency key, so a retry
 * converges on one subscription instead of creating another.
 */
const CheckoutQuoteFields = {
  expected_variant: Schema.optional(Schema.NullOr(Schema.String)),
  billing_interval: Schema.optional(
    Schema.NullOr(Schema.Literals(['month', 'year'])),
  ),
  checkout_attempt_id: Schema.optional(
    Schema.NullOr(
      Schema.String.pipe(Schema.check(Schema.isPattern(/^[\w-]{1,64}$/))),
    ),
  ),
};

export const ResubscribeRequest = Schema.Struct({
  price_id: Schema.optional(Schema.String),
  skip_existing_payment_method: Schema.optional(Schema.Boolean),
  ...CheckoutAttributionFields,
  ...CheckoutQuoteFields,
});

/**
 * POST /stripe/resubscribe
 * Subscribe or resubscribe a user to Stripe.
 * Creates a Stripe customer if needed. Uses existing payment method if available,
 * otherwise returns a payment_intent_client_secret for collecting payment info.
 */
const resubscribeEndpoint = HttpApiEndpoint.post(
  'Resubscribe',
  '/resubscribe',
  {
    payload: ResubscribeRequest,
    success: PostResubscribe200,
    error: [BadRequestError, ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Resubscribe to Stripe')
  .annotate(
    OpenApi.Description,
    'Subscribes or resubscribes a user to Stripe. Creates a Stripe customer if needed. If a payment method is on file, uses it. Otherwise returns a payment_intent_client_secret for collecting payment info.',
  );

/**
 * Response shape for `SuccessfulPaymentIntent`.
 *
 * Extends the legacy shape (`payment_intent_client_secret`) with the
 * fields the /post-purchase route loader needs to decide which
 * conversion pixels to fire and at what value:
 *
 * - `amount_usd` — real revenue at this moment, sourced from
 *   `subscription.items[0].price.unit_amount`. **Null when the
 *   subscription is not `active`** (so trialing subs do NOT report a
 *   Purchase value). Resolved independently of the PostHog
 *   `user_subscription_payment_success` event, which the `invoice.paid`
 *   webhook emits from the invoice total.
 * - `currency` — three-letter currency code, paired with `amount_usd`.
 * - `trial_started` — true iff `subscription.status === 'trialing'`.
 *   Sourced from Stripe, NOT from the frontend
 *   `useFreeTrialAvailable()` config flag — that flag is a UI gate;
 *   this boolean is what Stripe actually has, which is the only thing
 *   the conversion-pixel firing logic should trust.
 *
 * `amount_usd` and `currency` can both be null on the same response
 * (sub-already-active early-return, ghost redirect, malformed price);
 * callers must skip the Purchase-pixel branch in that case rather
 * than substitute a default. `trial_started` is independent — a
 * trialing sub has `amount_usd: null` AND `trial_started: true`.
 */
const SuccessfulPaymentIntentResponse = Schema.Struct({
  payment_intent_client_secret: Schema.NullOr(Schema.String),
  amount_usd: Schema.NullOr(
    Schema.Finite.pipe(
      Schema.check(Schema.isFinite()),
      Schema.check(Schema.isGreaterThan(0)),
    ),
  ),
  currency: Schema.NullOr(Schema.String),
  trial_started: Schema.Boolean,
});

/**
 * POST /stripe/successful-payment-intent
 * Confirms a successful payment intent to prevent race conditions with webhooks.
 */
const successfulPaymentIntentEndpoint = HttpApiEndpoint.post(
  'SuccessfulPaymentIntent',
  '/successful-payment-intent',
  {
    success: SuccessfulPaymentIntentResponse,
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Confirm successful payment intent')
  .annotate(
    OpenApi.Description,
    'Called after a successful Stripe payment to confirm the subscription is active. Prevents race conditions while waiting for Stripe webhooks. Returns the resolved paid amount + currency so /post-purchase conversion pixels report the actual revenue (not a hardcoded default).',
  );

// ============================================================================
// Money Endpoints (legacy DTO compat)
// ============================================================================

const PaymentDetailsResponse = Schema.Union([
  Schema.Struct({
    status: Schema.Literal('success'),
    nextPaymentDate: Schema.String,
  }),
  Schema.Struct({
    status: Schema.Literal('failed'),
  }),
]);

const getPaymentDetailsEndpoint = HttpApiEndpoint.get(
  'GetPaymentDetails',
  '/getPaymentDetails',
  {
    success: PaymentDetailsResponse,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Get payment details (legacy)')
  .annotate(
    OpenApi.Description,
    'Returns the next payment date for the user. Legacy DTO shape for web client compat.',
  );

export const moneyGroup = HttpApiGroup.make('Money')
  .add(getPaymentDetailsEndpoint)
  .prefix('/money');

// ============================================================================
// Stripe Endpoints
// ============================================================================

/**
 * Local additive replacement for the generated
 * `PostCreatePaidSubscriptionRequest`, which cannot carry the quote fields
 * without regenerating `legacy-client.gen.ts` from a schema that is not
 * deployed yet. Same wire shape plus `CheckoutQuoteFields`; `price_id` and
 * `trial_period_days` are kept for the compatibility window as expected
 * values, and are removed with the old client rather than during the ramp.
 */
export const CreatePaidSubscriptionRequest = Schema.Struct({
  price_id: Schema.optional(Schema.NullOr(Schema.String)),
  trial_period_days: Schema.optional(Schema.NullOr(Schema.Int)),
  ...CheckoutAttributionFields,
  ...CheckoutQuoteFields,
});

/**
 * POST /stripe/create-paid-subscription
 * Create a subscription with a free trial. Returns a setup intent client secret
 * for the frontend to call stripe.confirmSetup().
 */
const createPaidSubscriptionEndpoint = HttpApiEndpoint.post(
  'CreatePaidSubscription',
  '/create-paid-subscription',
  {
    payload: CreatePaidSubscriptionRequest,
    success: PostCreatePaidSubscription200,
    error: [BadRequestError, ConflictError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)

  .annotate(OpenApi.Summary, 'Create paid subscription with free trial')
  .annotate(
    OpenApi.Description,
    'Creates a Stripe subscription with a free trial period. Returns a setup intent client secret for the frontend to collect payment details via stripe.confirmSetup().',
  );

/**
 * POST /stripe/webhook
 * Stripe webhook for subscription lifecycle events. No auth — uses Stripe signature verification.
 */
const stripeWebhookEndpoint = HttpApiEndpoint.post(
  'StripeWebhook',
  '/webhook',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [BadRequestError, InternalServerError],
  },
)

  .annotate(OpenApi.Summary, 'Stripe webhook')
  .annotate(
    OpenApi.Description,
    'Handles Stripe webhook events (customer.subscription.updated, invoice.paid). Verified via Stripe signature.',
  );

export const stripeGroup = HttpApiGroup.make('Stripe')
  .add(cancelSubscriptionEndpoint)
  .add(createPaidSubscriptionEndpoint)
  .add(modifyPaymentMethodEndpoint)
  .add(modifySubscriptionEndpoint)
  .add(resubscribeEndpoint)
  .add(successfulPaymentIntentEndpoint)
  .add(stripeWebhookEndpoint)
  .prefix('/stripe');

// ============================================================================
// Webhooks Group (RevenueCat)
// ============================================================================

/**
 * POST /webhooks/revenutCat
 * RevenueCat webhook for mobile subscription lifecycle events. No auth.
 * Note: the path contains a legacy typo ("revenut" instead of "revenue") that is
 * baked into RevenueCat's webhook configuration and cannot be changed.
 */
const revenueCatWebhookEndpoint = HttpApiEndpoint.post(
  'RevenueCatWebhook',
  '/revenutCat',
  {
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: InternalServerError,
  },
)

  .annotate(OpenApi.Summary, 'RevenueCat webhook')
  .annotate(
    OpenApi.Description,
    'Handles RevenueCat webhook events (INITIAL_PURCHASE, RENEWAL, CANCELLATION, PRODUCT_CHANGE, TRANSFER).',
  );

export const webhooksGroup = HttpApiGroup.make('Webhooks')
  .add(revenueCatWebhookEndpoint)
  .prefix('/webhooks');
